import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Alert,
  CircularProgress,
  Container,
  Fade,
  Slide,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
  Divider,
  alpha
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Business as BusinessIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  Login as LoginIcon,
  Clear as ClearIcon,
  Edit as EditIcon
} from '@mui/icons-material';

interface LoginPageProps {
  onLoginSuccess: (token: string, user: any) => void;
}

export const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  // const isMobile = useMediaQuery(useTheme().breakpoints.down('sm')); // TODO: Use for responsive design
  const [tenantName, setTenantName] = useState('');
  const [userCode, setUserCode] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isTenantSaved, setIsTenantSaved] = useState(false);
  const [isEditingTenant, setIsEditingTenant] = useState(false);

  // Load saved tenant name on component mount
  React.useEffect(() => {
    const savedTenant = localStorage.getItem('tenant_name');
    if (savedTenant) {
      setTenantName(savedTenant);
      setIsTenantSaved(true);
      setIsEditingTenant(false);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!tenantName.trim()) {
      setError('يرجى إدخال اسم المؤسسة');
      return;
    }
    
    setIsLoading(true);
    setError(null);

    try {
      // Construct tenant-specific API URL using helper
      const { getTenantApiUrl } = await import('../../utils/api');
      const apiUrl = `${getTenantApiUrl(tenantName)}/v1/auth/login`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_code: userCode,
          password: password,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.data?.access_token) {
          // Store token and user data
          localStorage.setItem('auth_token', data.data.access_token);
          localStorage.setItem('auth_user', JSON.stringify(data.data.user));
          localStorage.setItem('tenant_name', tenantName);
          
          // Call success callback
          onLoginSuccess(data.data.access_token, data.data.user);
        } else {
          setError(data.message || 'فشل تسجيل الدخول');
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'فشل تسجيل الدخول');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('خطأ في الاتصال بالخادم');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Fade in timeout={1000}>
          <Card
            elevation={24}
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              position: 'relative',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: `0 20px 60px ${alpha('#000', 0.3)}`,
            }}
          >
            {/* Header with Logo */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                color: 'white',
                p: 4,
                textAlign: 'center',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(255, 255, 255, 0.1)',
                  clipPath: 'polygon(0 0, 100% 0, 85% 100%, 0% 100%)',
                },
              }}
            >
              <BusinessIcon sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h4" component="h1" fontWeight="bold">
                نظام خبير
              </Typography>
              <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                نظام الحسابات المتكامل
              </Typography>
            </Box>

            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" component="h2" gutterBottom textAlign="center" fontWeight="600">
                تسجيل الدخول
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 4 }}>
                ادخل بيانات تسجيل الدخول والمؤسسة
              </Typography>

              {error && (
                <Slide direction="down" in={!!error} mountOnEnter unmountOnExit>
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mb: 3,
                      borderRadius: 2,
                      '& .MuiAlert-message': {
                        fontWeight: 500
                      }
                    }}
                  >
                    {error}
                  </Alert>
                </Slide>
              )}

              <Box component="form" onSubmit={handleLogin}>
                <TextField
                  fullWidth
                  label="اسم المؤسسة"
                  value={tenantName}
                  onChange={(e) => {
                    setTenantName(e.target.value);
                    if (isTenantSaved && !isEditingTenant) {
                      setIsEditingTenant(true);
                    }
                  }}
                  required
                  disabled={isLoading || (isTenantSaved && !isEditingTenant)}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon color={isTenantSaved && !isEditingTenant ? "disabled" : "primary"} />
                      </InputAdornment>
                    ),
                    endAdornment: tenantName && (
                      <InputAdornment position="end">
                        {isTenantSaved && !isEditingTenant ? (
                          <IconButton
                            onClick={() => setIsEditingTenant(true)}
                            edge="end"
                            size="small"
                            disabled={isLoading}
                            title="تعديل اسم المؤسسة"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          <IconButton
                            onClick={() => {
                              setTenantName('');
                              localStorage.removeItem('tenant_name');
                              setIsTenantSaved(false);
                              setIsEditingTenant(false);
                            }}
                            edge="end"
                            size="small"
                            disabled={isLoading}
                            title="مسح اسم المؤسسة"
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                  helperText={
                    isTenantSaved && !isEditingTenant 
                      ? "تم حفظ اسم المؤسسة مسبقاً - اضغط على أيقونة التعديل للتغيير"
                      : isEditingTenant 
                        ? "يمكنك تعديل اسم المؤسسة الآن"
                        : undefined
                  }
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-disabled': {
                        '& fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                        '& input': {
                          color: 'rgba(0, 0, 0, 0.6)',
                          WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                        },
                      },
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="كود المستخدم"
                  value={userCode}
                  onChange={(e) => setUserCode(e.target.value)}
                  required
                  disabled={isLoading}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                  dir="ltr"
                />

                <TextField
                  fullWidth
                  label="كلمة المرور"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={isLoading}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 4,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                  dir="ltr"
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 25px ${alpha('#1976d2', 0.4)}`,
                    },
                    '&:disabled': {
                      background: 'grey.300',
                      transform: 'none',
                      boxShadow: 'none',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  endIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                >
                  {isLoading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
                </Button>
              </Box>
            </CardContent>

            {/* Footer */}
            <Divider />
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                © 2025 نظام خبير - جميع الحقوق محفوظة
              </Typography>
            </Box>
          </Card>
        </Fade>
      </Container>
    </Box>
  );
};